import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "./CustomButton";
import Loader from "../dist/Loader";
import formatDate from "../utils/formatDate";
import XLSX from "xlsx-js-style";

const EmployeePOBSOBTargetQuarterly = () => {
  const tableRef = useRef(null);
  const [states, setStates] = useState([{ id: "All", name: "All" }]);
  const [divisions, setDivisions] = useState([{ id: "All", name: "All" }]);
  const [employees, setEmployees] = useState([
    { id: "All", first_name: "All", last_name: "" },
  ]);
  const [selectedState, setSelectedState] = useState("All");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");
  const [selectedQuarter, setSelectedQuarter] = useState("");
  const [quarters, setQuarters] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    const startYear = 2018;
    const allQuarters = [];

    for (let year = startYear; year <= currentYear; year++) {
      allQuarters.push(
        {
          label: `01/Jan/${year} - 31/Mar/${year}`,
          value: `Q1-${year}`,
          months: [1, 2, 3],
        },
        {
          label: `01/Apr/${year} - 30/Jun/${year}`,
          value: `Q2-${year}`,
          months: [4, 5, 6],
        },
        {
          label: `01/Jul/${year} - 30/Sep/${year}`,
          value: `Q3-${year}`,
          months: [7, 8, 9],
        },
        {
          label: `01/Oct/${year} - 31/Dec/${year}`,
          value: `Q4-${year}`,
          months: [10, 11, 12],
        }
      );
    }

    const active =
      allQuarters.find(
        (q) =>
          q.value.startsWith("Q") &&
          q.months.includes(currentMonth) &&
          q.value.endsWith(`-${currentYear}`)
      ) || allQuarters[0];

    setQuarters(allQuarters);
    setSelectedQuarter(active.value);
  }, []);

  useEffect(() => {
    let mounted = true;
    const init = async () => {
      try {
        const [statesRes, divRes, empRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() }),
          axios.get(`${BASE_URL}/api/divisions`, { headers: getAuthHeaders() }),
          axios.get(`${BASE_URL}/api/employees`, { headers: getAuthHeaders() }),
        ]);
        if (!mounted) return;
        setStates([
          { id: "All", name: "All" },
          ...(Array.isArray(statesRes.data) ? statesRes.data : []),
        ]);
        setDivisions([
          { id: "All", name: "All" },
          ...(Array.isArray(divRes.data) ? divRes.data : []),
        ]);
        setEmployees([
          { id: "All", first_name: "All", last_name: "" },
          ...(Array.isArray(empRes.data) ? empRes.data : []),
        ]);
      } catch (err) {
        if (!mounted) return;
        setError("Failed to load dropdowns");
      }
    };
    init();
    return () => {
      mounted = false;
    };
  }, []);

  const fetchEmployees = useCallback(
    async (stateId = "All", divisionName = "All") => {
      try {
        let url = `${BASE_URL}/api/employees`;
        const params = [];
        if (stateId && stateId !== "All")
          params.push(`state_id=${encodeURIComponent(stateId)}`);
        if (divisionName && divisionName !== "All")
          params.push(`division=${encodeURIComponent(divisionName)}`);
        if (params.length) url += `?${params.join("&")}`;
        const res = await axios.get(url, { headers: getAuthHeaders() });
        setEmployees([
          { id: "All", first_name: "All", last_name: "" },
          ...(Array.isArray(res.data) ? res.data : []),
        ]);
        setSelectedEmployee("All");
      } catch (err) {
        setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
      }
    },
    []
  );

  useEffect(() => {
    let mounted = true;
    const fetchDivisions = async () => {
      try {
        let url = `${BASE_URL}/api/divisions`;
        if (selectedState && selectedState !== "All") {
          url = `${BASE_URL}/api/divisions?state_id=${encodeURIComponent(
            selectedState
          )}`;
        }
        const res = await axios.get(url, { headers: getAuthHeaders() });
        if (!mounted) return;
        setDivisions([
          { id: "All", name: "All" },
          ...(Array.isArray(res.data) ? res.data : []),
        ]);
        setSelectedDivision("All");
        fetchEmployees(selectedState || "All", "All");
      } catch (err) {
        if (!mounted) return;
        setDivisions([{ id: "All", name: "All" }]);
      }
    };
    fetchDivisions();
    return () => {
      mounted = false;
    };
  }, [selectedState, fetchEmployees]);

  const handleStateChange = (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("All");
    setSelectedEmployee("All");
    setError(null);
  };

  const handleDivisionChange = (divisionId) => {
    setSelectedDivision(divisionId);
    setSelectedEmployee("All");
    setError(null);
    const divisionName =
      divisionId && divisionId !== "All"
        ? divisions.find((d) => String(d.id) === String(divisionId))?.name || "All"
        : "All";
    fetchEmployees(selectedState || "All", divisionName);
  };

  const handleEmployeeChange = (employeeId) => {
    setSelectedEmployee(employeeId);
    setError(null);
  };

  const handleQuarterChange = (q) => {
    setSelectedQuarter(q);
    setError(null);
  };

  const handleGoClick = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // FIX 1: activeQuarterObj was used in handleExport but derived AFTER
      // handleGoClick — here we derive it locally so params are always correct.
      const chosenQuarter =
        quarters.find((q) => q.value === selectedQuarter) || quarters[0];

      if (!chosenQuarter) {
        setError("Please select a valid quarter.");
        setIsLoading(false);
        return;
      }

      const params = {
        year: parseInt(chosenQuarter.value.split("-")[1], 10),
        months: chosenQuarter.months.join(","),
      };

      if (selectedState && selectedState !== "All")
        params.state_id = selectedState;

      if (selectedDivision && selectedDivision !== "All") {
        const division = divisions.find(
          (d) => String(d.id) === String(selectedDivision)
        );
        if (division?.name) {
          params.division_id = division.name;
        }
      }

      if (selectedEmployee && selectedEmployee !== "All")
        params.employee_id = selectedEmployee;

      const response = await axios.get(
        `${BASE_URL}/api/employee/quarterly-target`,
        { params, headers: getAuthHeaders() }
      );

      setTableData(
        Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : []
      );
      setShowTable(true);
    } catch (err) {
      setError("Failed to load data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const formatNumber = (num) =>
  num || num === 0
    ? Math.round(Number(num)).toLocaleString()
    : "0";

  const handlePrint = () => {
    const printContent = tableRef.current?.innerHTML || "";
    const printWindow = window.open("", "_blank", "width=900,height=600");
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Print Table</title>
            <style> body{font-family:Arial;padding:20px} table{width:100%;border-collapse:collapse} th,td{border:1px solid #ccc;padding:8px;text-align:left} th{background:#f2f2f2} </style>
          </head>
          <body>
            <h2>Employee POB vs SOB</h2>
            ${printContent}
            <script>window.onload = function(){ window.print(); }</script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

 const handleExport = async () => {
  try {
    if (!tableData.length) {
      setError("No data to export");
      return;
    }

    const exportQuarterObj =
      quarters.find((q) => q.value === selectedQuarter) ||
      quarters[0] || { months: [] };
    const exportYear = selectedQuarter
      ? parseInt(selectedQuarter.split("-")[1], 10)
      : new Date().getFullYear();

    const wb = XLSX.utils.book_new();
    const ws = {};

    const headers = [
      "S.No",
      "Emp ID",
      "State",
      "Employee Name",
      "Desi",
      "HQ",
      "Div",
      "DOJ",
      "Resignation Date",
      "Mobile",
      "Manager",
    ];

    exportQuarterObj.months.forEach((monthNum) => {
      const date = new Date(exportYear, monthNum - 1);
      const label = date.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
      headers.push(`${label} Target POB`);
      headers.push(`${label} POB`);
      headers.push(`${label} Target SOB`);
      headers.push(`${label} SOB`);
    });

    const headerStyle = {
      font: {
        bold: true,
        color: { rgb: "FFFFFF" },
      },
      fill: {
        fgColor: { rgb: "2563EB" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
        wrapText: true,
      },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    const rowStyle = {
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    const setDateCell = (r, c, rawVal, s) => {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (rawVal) {
        try {
          const date = new Date(
            /^\d{4}-\d{2}-\d{2}$/.test(String(rawVal))
              ? `${rawVal}T00:00:00`
              : rawVal
          );
          if (!isNaN(date.getTime())) {
            ws[addr] = { v: date, t: "d", z: "dd-mmm-yyyy", s };
            return;
          }
        } catch {}
      }
      ws[addr] = { v: "", t: "s", s };
    };

    headers.forEach((header, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIndex });
      ws[cellAddress] = { v: header, t: "s", s: headerStyle };
    });

    const DOJ_COL = 7;
    const RESIGN_COL = 8;

    tableData.forEach((item, index) => {
      const empRaw = String(item.employee ?? "").trim();
      const empIsNum = /^\d+$/.test(empRaw);
      const empID = empIsNum ? Number(empRaw) : empRaw;

      const rowData = [
        index + 1,
        empID,
        item.state_name || "",
        item.employee_name || "",
        item.employee_designation || "",
        item.headquarter_name || "",
        item.employee_division || "",
        null,
        null,
        item.mobile || "",
        item.employee_manager || "",
      ];

      exportQuarterObj.months.forEach((month) => {
        rowData.push(Math.round(Number(item[`pobtarget_${month}`] || 0)));
        rowData.push(Math.round(Number(item[`pob_${month}`] || 0)));
        rowData.push(Math.round(Number(item[`sobtarget_${month}`] || 0)));
        rowData.push(Math.round(Number(item[`sob_${month}`] || 0)));
      });

      rowData.forEach((value, colIndex) => {
        if (colIndex === DOJ_COL || colIndex === RESIGN_COL) return;

        const cellAddress = XLSX.utils.encode_cell({
          r: index + 1,
          c: colIndex,
        });
        ws[cellAddress] = {
          v: value,
          t:
            colIndex === 1
              ? empIsNum
                ? "n"
                : "s"
              : typeof value === "number"
                ? "n"
                : "s",
          s: rowStyle,
        };
      });

      setDateCell(index + 1, DOJ_COL, item.doj, rowStyle);
      setDateCell(index + 1, RESIGN_COL, item.resignation_date, rowStyle);
    });

    const totalRow = tableData.length + 2;

    ws[`A${totalRow}`] = {
      v: "GRAND TOTAL",
      t: "s",
      s: headerStyle,
    };

    const quarterColsStart = 11;
    const totalCols = exportQuarterObj.months.length * 4;

    for (let col = quarterColsStart; col < quarterColsStart + totalCols; col++) {
      const colLetter = XLSX.utils.encode_col(col);
      ws[`${colLetter}${totalRow}`] = {
        f: `SUM(${colLetter}2:${colLetter}${tableData.length + 1})`,
        t: "n",
        s: headerStyle,
      };
    }

    ws["!ref"] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: totalRow, c: headers.length - 1 },
    });

    ws["!cols"] = [
      { wch: 8 },
      { wch: 12 },
      { wch: 15 },
      { wch: 28 },
      { wch: 15 },
      { wch: 18 },
      { wch: 18 },
      { wch: 14 },
      { wch: 16 },
      { wch: 15 },
      { wch: 24 },
      ...Array.from(
        { length: exportQuarterObj.months.length * 4 },
        () => ({ wch: 14 })
      ),
    ];

    ws["!freeze"] = { xSplit: 0, ySplit: 1 };

    ws["!autofilter"] = {
      ref: `A1:${XLSX.utils.encode_col(headers.length - 1)}1`,
    };

    XLSX.utils.book_append_sheet(wb, ws, "Employee POB SOB");
    XLSX.writeFile(wb, "employee_pob_sob.xlsx", { compression: true });
  } catch (err) {
    setError("Failed to export data. Please try again.");
  }
};

  const calculateMonthlyTotals = (data, months) => {
    return (months || []).map((month) => ({
      month,
      pobtarget: data.reduce(
        (sum, item) => sum + (Number(item[`pobtarget_${month}`]) || 0),
        0
      ),
      pob: data.reduce(
        (sum, item) => sum + (Number(item[`pob_${month}`]) || 0),
        0
      ),
      sobtarget: data.reduce(
        (sum, item) => sum + (Number(item[`sobtarget_${month}`]) || 0),
        0
      ),
      sob: data.reduce(
        (sum, item) => sum + (Number(item[`sob_${month}`]) || 0),
        0
      ),
    }));
  };

  // FIX 7: Moved activeQuarterObj and activeYear ABOVE the return statement
  // so they are in scope for both the JSX and the export/print handlers.
  // In the original code these were defined after return which caused
  // handleExport to crash — JS hoists var but NOT const/let.
  const activeQuarterObj =
    quarters.find((q) => q.value === selectedQuarter) ||
    quarters[0] || { months: [] };

  const activeYear = selectedQuarter
    ? parseInt(selectedQuarter.split("-")[1], 10)
    : new Date().getFullYear();

  return (
    <div className="p-2 border-2 border-gray-300 rounded-lg bg-white">
      {isLoading && <Loader onCancel={() => setIsLoading(false)} />}
      <div className="max-w-full p-4 bg-gray-50 border border-gray-300 rounded-lg shadow-sm">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">Employee</span>{" "}
          <span className="text-blue-500">POB VS SOB</span>
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center mb-3">
          <label className="hidden md:block text-right">State</label>
          <select
            className="w-full p-2 border rounded text-sm"
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
            disabled={isLoading}
          >
            <option value="All">All</option>
            {states
              .filter((s) => s.id !== "All")
              .map((state) => (
                <option key={state.id} value={state.id}>
                  {state.name}
                </option>
              ))}
          </select>

          <label className="hidden md:block text-right">Division</label>
          <select
            className="w-full p-2 border rounded text-sm"
            value={selectedDivision}
            onChange={(e) => handleDivisionChange(e.target.value)}
            disabled={isLoading}
          >
            <option value="All">All</option>
            {divisions
              .filter((d) => d.id !== "All")
              .map((div) => (
                <option key={div.id} value={div.id}>
                  {div.name}
                </option>
              ))}
          </select>

          <div />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
          <label className="hidden md:block text-right">Employee</label>
          <select
            className="w-full p-2 border rounded text-sm"
            value={selectedEmployee}
            onChange={(e) => handleEmployeeChange(e.target.value)}
            disabled={isLoading}
          >
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.id === "All"
                  ? "All"
                  : `${emp.first_name || ""} ${emp.last_name || ""} | ${emp.employee} | ${emp.designation ? ` | ${emp.designation}` : ""
                  }${emp.headquarter_name ? ` | ${emp.headquarter_name}` : ""}`}
              </option>
            ))}
          </select>

          <label className="hidden md:block text-right">Quarter:</label>
          <select
            className="w-full p-2 border rounded text-sm"
            value={selectedQuarter}
            onChange={(e) => handleQuarterChange(e.target.value)}
            disabled={isLoading}
          >
            {quarters.map((option, idx) => (
              <option key={idx} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <div />
        </div>

        <div className="flex justify-center mt-4">
          <CustomButton
            variant="go"
            onClick={handleGoClick}
            disabled={isLoading}
            className="break-normal"
          >
            {isLoading ? "Loading..." : "GO"}
          </CustomButton>
        </div>

        {error && (
          <div className="mt-3 text-center text-sm text-red-600">{error}</div>
        )}
      </div>

      {showTable && (
        <div className="mt-4 bg-white border border-gray-300 rounded-lg p-4">
          <div className="border rounded shadow table-wrapper" ref={tableRef}>
            <table className="unlock-dcr-leave-table text-sm">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="px-3 py-2 text-left">S.No</th>
                  <th className="px-3 py-2 text-left">Emp ID</th>
                  <th className="px-3 py-2 text-left">State</th>
                  <th className="px-3 py-2 text-left">Employee Name</th>
                  <th className="px-3 py-2 text-left">Desi</th>
                  <th className="px-3 py-2 text-left">HQ</th>
                  <th className="px-3 py-2 text-left">Div</th>
                  <th className="px-3 py-2 text-left">Doj</th>
                  <th className="px-3 py-2 text-left">Mobile</th>
                  <th className="px-3 py-2 text-left">Manager</th>
                  {activeQuarterObj.months.map((monthNum) => {
                    const date = new Date(activeYear, monthNum - 1);
                    const label = date.toLocaleString("default", {
                      month: "short",
                      year: "numeric",
                    });
                    return (
                      <React.Fragment key={monthNum}>
                        <th className="px-3 py-2 text-left">
                          {label} Target POB
                        </th>
                        <th className="px-3 py-2 text-left">{label} POB</th>
                        <th className="px-3 py-2 text-left">
                          {label} Target SOB
                        </th>
                        <th className="px-3 py-2 text-left">{label} SOB</th>
                      </React.Fragment>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((item, index) => (
                    <tr
                      key={item.employee_id || index}
                      className="odd:bg-white even:bg-slate-50 align-top"
                    >
                      <td className="px-3 py-2">{index + 1}</td>
                      <td className="px-3 py-2">{item.employee}</td>
                      <td className="px-3 py-2">{item.state_name}</td>
                      <td className="px-3 py-2">{item.employee_name}</td>
                      <td className="px-3 py-2">{item.employee_designation}</td>
                      <td className="px-3 py-2">{item.headquarter_name}</td>
                      <td className="px-3 py-2">{item.employee_division}</td>
                      <td className="px-3 py-2">{formatDate(item.doj)}</td>
                      <td className="px-3 py-2">{item.mobile}</td>
                      <td className="px-3 py-2">{item.employee_manager}</td>
                      {activeQuarterObj.months.map((month) => (
                        <React.Fragment key={month}>
                          <td className="px-3 py-2">
                            {formatNumber(item[`pobtarget_${month}`] || 0)}
                          </td>
                          {/* FIX 8: pob_{month} was showing 0 in table because
                              the API returns floats but the old code had a
                              string coercion issue from the previous pob string
                              bug ("299991.00"). Now the API returns proper floats
                              so Number() conversion here handles it correctly. */}
                          <td className="px-3 py-2">
                            {formatNumber(item[`pob_${month}`] || 0)}
                          </td>
                          <td className="px-3 py-2">
                            {formatNumber(item[`sobtarget_${month}`] || 0)}
                          </td>
                          <td className="px-3 py-2">
                            {formatNumber(item[`sob_${month}`] || 0)}
                          </td>
                        </React.Fragment>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={10 + activeQuarterObj.months.length * 4}
                      className="px-3 py-6 text-center text-sm text-gray-600"
                    >
                      No data available for the selected filters
                    </td>
                  </tr>
                )}

                {tableData.length > 0 && (
                  <tr>
                    <td
                      colSpan={10}
                      className="bg-blue-600 px-3 py-2 text-white font-bold text-right"
                    >
                      GRAND TOTAL
                    </td>
                    {calculateMonthlyTotals(
                      tableData,
                      activeQuarterObj.months
                    ).map((monthData) => (
                      <React.Fragment key={monthData.month}>
                        <td className="px-3 py-2 font-semibold text-white bg-blue-600">
                          {formatNumber(monthData.pobtarget)}
                        </td>
                        <td className="px-3 py-2 font-semibold text-white bg-blue-600">
                          {formatNumber(monthData.pob)}
                        </td>
                        <td className="px-3 py-2 font-semibold text-white bg-blue-600">
                          {formatNumber(monthData.sobtarget)}
                        </td>
                        <td className="px-3 py-2 font-semibold text-white bg-blue-600">
                          {formatNumber(monthData.sob)}
                        </td>
                      </React.Fragment>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex gap-4 justify-center">
            <CustomButton onClick={handlePrint} label="Print" variant="print">
              🖨
            </CustomButton>
            <CustomButton onClick={handleExport} label="Export" variant="export">
              📥
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeePOBSOBTargetQuarterly;
